import { Utility } from "@/src/utils/utility";
import { Article, ItemList, Product, WithContext } from "schema-dts";

export const generateProductListSchema = ({
  productData,
  url,
}: {
  productData: any;
  url?: string;
}) => {
  const jsonLd: WithContext<ItemList> = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement:
      productData?.map((product: any, index: number) => ({
        "@type": "ListItem",
        position: index + 1, // Position in the list
        url:
          Utility.generateAmazonAffiliateUrl(
            product.productLink[0]?.affiliateUrl,
            product?.title,
          ) ||
          product.productLink[0]?.affiliateUrl ||
          url ||
          "#",
        item: {
          "@type": "Product",
          name: product?.title || "Unnamed Product", // Product name
          image:
            product?.imageUrls[0] ||
            `${process.env.NEXT_PUBLIC_BASE_URL}/opengraph-image.png`, // Product image
          description:
            product?.description?.join(" ")?.slice(0, 160) ||
            product?.title?.slice(0, 160), // Short description
          brand: {
            "@type": "Brand",
            name: product?.brand || "Amazon",
          },
          // "sku": product?.sku || product?.asin, // Add unique identifier
          // "mpn": product?.mpn || product?.asin, // Manufacturer Part Number
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price:
              product?.productPrice?.find((e: any) => e?.currency === "USD")
                ?.price || 0,
            availability: "https://schema.org/InStock",
            // "availability": product?.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", // Dynamic stock status
            url:
              Utility.generateAmazonAffiliateUrl(
                product.productLink[0]?.affiliateUrl,
                product?.title,
              ) ||
              product.productLink[0]?.affiliateUrl ||
              "#", // Amazon affiliate link
            itemCondition: "https://schema.org/NewCondition",
            seller: {
              "@type": "Organization",
              name: "Amazon",
            },
          },
          // Add reviews/ratings if available (example):
          aggregateRating: product?.reviews
            ? {
                "@type": "AggregateRating",
                ratingValue: product?.reviews?.avgRating,
                reviewCount: product?.reviews?.total,
              }
            : undefined,
        },
      })) || [],
    image: `${process.env.NEXT_PUBLIC_BASE_URL}/opengraph-image.png`,
  };

  return jsonLd;
};

export const generateSingleProductSchema = ({
  product,
  slug,
}: {
  product: any;
  slug: any;
}) => {
  const jsonLd: WithContext<Product> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product?.title || "Starlightopia",
    url: `${process.env.NEXT_PUBLIC_BASE_URL}/product/${slug}`,
    image: `${product?.imageUrls[0] || `${process.env.NEXT_PUBLIC_BASE_URL}/opengraph-image.png`}`,
    description:
      product?.description?.join(" ") || "A great product available now.",
    brand: {
      "@type": "Brand",
      name: product?.brand || "Amazon", // Include brand name if available
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price:
        product?.productPrice?.find((e: any) => e?.currency === "USD")?.price ||
        0,
      availability: "https://schema.org/InStock",
      url:
        Utility.generateAmazonAffiliateUrl(
          product.productLink[0]?.affiliateUrl,
          product?.title,
        ) ||
        product?.productLink[0]?.affiliateUrl ||
        "#",
      itemCondition: "https://schema.org/NewCondition",
      seller: {
        "@type": "Organization",
        name: product?.sellerName || product?.brand || "Trusted Seller",
      },
    },
    // aggregateRating: {
    //   "@type": "AggregateRating",
    //   ratingValue: Math.max(1, product?.reviews?.averageRating || 0), // enforce minimum rating of 1
    //   reviewCount: product?.reviews?.totalReviews || 0,
    //   bestRating: 5,
    //   worstRating: 1,
    // },
    aggregateRating:
      product?.reviews?.totalReviews >= 1
        ? {
            "@type": "AggregateRating",
            ratingValue: Math.max(1, product?.reviews?.averageRating || 0),
            reviewCount: product.reviews.totalReviews,
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
    review: Array.isArray(product?.reviews?.individualReviews)
      ? product?.reviews?.individualReviews?.map((review: any) => ({
          "@type": "Review",
          author: {
            "@type": "Person",
            name: review?.username || "Anonymous",
          },
          reviewBody: review?.comment || "Great product!",
          reviewRating: {
            "@type": "Rating",
            ratingValue: String(Math.max(1, review?.rating || 0)),
            bestRating: "5",
            worstRating: "1",
          },
        }))
      : [],
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Category",
        value: product?.categoryIds[0]?.name || "General",
      },
      {
        "@type": "PropertyValue",
        name: "Features",
        value: product?.features?.join(", ") || "Standard features",
      },
    ],
  };

  return jsonLd;
};

export const generateProductArticleSchema = ({
  product,
  slug,
}: {
  product: any;
  slug: any;
}) => {
  const articleJsonLd: WithContext<Article> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      product?.title ||
      `Starlightopia Review: Best Product for ${new Date().getFullYear()}`,
    description:
      product?.description?.join(" ") ||
      "In-depth analysis of Starlightopia features...",
    datePublished: product?.createdAt || `${new Date().toDateString()}`,
    dateModified: product?.updatedAt || `${new Date().toDateString()}`,
    author: {
      "@type": "Person",
      name: product?.author || "Admin",
      url: `#`,
    },
    publisher: {
      "@type": "Organization",
      name: "Starlightopia",
      logo: {
        "@type": "ImageObject",
        url: `${process.env.NEXT_PUBLIC_BASE_URL}/favicon.ico`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${process.env.NEXT_PUBLIC_BASE_URL}/product/${slug}`,
    },
    image: product?.imageUrls?.slice(0, 3).map((e: any) => e), // Include product images if relevant
  };

  return articleJsonLd;
};
